import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { finalizePayment } from "../services/paymentsApi";
import { translatePaymentFailure } from "../utils/paymentFailure";
import PaymentResultModal from "../components/payment/PaymentResultModal";

/**
 * Public route hit by Kapital Bank after the customer completes (or cancels) the HPP flow.
 *
 * URL shape: /payment/callback?ID=<kapitalOrderId>&STATUS=FullyPaid|Declined|...
 *
 * The query-string STATUS is advisory per the Kapital docs; we always re-verify by calling
 * POST /payments/{id}/finalize, which makes the backend GET /order/{id} the source of truth.
 *
 * Once finalized we show an informative result modal (success or failure). It has no action
 * beyond a Close button — closing routes the user back to where the purchase started
 * (their subscription page by default).
 */
const PaymentCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | success | failed
  const [message, setMessage] = useState("");
  // Where the Close button sends the user. Defaults to the subscription page, overridden
  // by the stored return path / the backend-confirmed return path.
  const destination = useRef("/settings/subscription");
  // useRef + early-return guard prevents React 18 StrictMode double-invocation
  // (which would otherwise call /finalize twice — backend is idempotent, but no need).
  const handled = useRef(false);

  // Map the payment purpose to a tailored success line so the modal actually tells the
  // user what they just bought, not a generic "done".
  const successMessageFor = (purpose) => {
    switch (purpose) {
      case "RENEWAL":
        return t("pages.subscription.paymentResult.successRenewal");
      case "PLAN_UPGRADE":
        return t("pages.subscription.paymentResult.successPlanUpgrade");
      case "ADDON_PURCHASE":
        return t("pages.subscription.paymentResult.successAddon");
      case "ONE_TIME_PACKAGE":
        return t("pages.subscription.paymentResult.successOneTime");
      default:
        return t("pages.subscription.paymentResult.successGeneric");
    }
  };

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const paymentTransactionId = sessionStorage.getItem("gredus.kapitalPaymentTxId");
    const returnPath = sessionStorage.getItem("gredus.kapitalReturnPath") || "/settings/subscription";
    destination.current = returnPath;

    // Kapital appends the outcome as an advisory query param. If the user CANCELLED on the payment
    // page there is nothing to settle — go straight back and do nothing (no modal, no charge). The
    // backend-verified result below is still the source of truth for the money side.
    const cbParams = new URLSearchParams(window.location.search);
    const kapitalStatus = (cbParams.get("STATUS") || cbParams.get("status") || "").toLowerCase();
    const looksCancelled = kapitalStatus.includes("cancel");

    if (!paymentTransactionId) {
      if (looksCancelled) {
        navigate(returnPath, { replace: true });
        return;
      }
      // No session — likely a stale tab or direct hit.
      setMessage(t("pages.subscription.paymentResult.sessionNotFound"));
      setStatus("failed");
      return;
    }

    (async () => {
      try {
        const result = await finalizePayment(paymentTransactionId);
        sessionStorage.removeItem("gredus.kapitalPaymentTxId");
        sessionStorage.removeItem("gredus.kapitalReturnPath");
        destination.current = result.returnPath || returnPath;

        if (result.success) {
          setMessage(successMessageFor(result.purpose));
          setStatus("success");
        } else if (
          result.abandoned ||
          looksCancelled ||
          (result.failureCode || "").toLowerCase().includes("cancel")
        ) {
          // User cancelled / went back without executing a charge — nothing was billed and no
          // payment record was kept. Return quietly (no failure modal).
          navigate(result.returnPath || returnPath, { replace: true });
        } else {
          setMessage(translatePaymentFailure(t, result.failureMessage));
          setStatus("failed");
        }
      } catch (e) {
        setMessage(
          e?.response?.data?.message || t("pages.subscription.paymentResult.failedGeneric")
        );
        setStatus("failed");
      }
    })();
    // We intentionally run this exactly once. The ref guard above handles double-mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    navigate(destination.current || "/settings/subscription", { replace: true });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: 24,
        background: "#f8fafc",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {status === "verifying" && (
        <>
          <div
            style={{
              width: 40,
              height: 40,
              border: "3px solid #e5e7eb",
              borderTopColor: "#2563eb",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <p style={{ color: "#374151" }}>{t("pages.subscription.paymentResult.verifying")}</p>
        </>
      )}

      <PaymentResultModal
        isOpen={status === "success" || status === "failed"}
        status={status}
        message={message}
        onClose={handleClose}
      />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default PaymentCallback;
